import { Container, Stack, Title, Text, Breadcrumbs, Anchor } from "@mantine/core";
import { IconGavel, IconChevronRight } from "@tabler/icons-react";
import Footer from "../../components/Footer";
import { AppEmail, AppWebsite } from "../../lib/data";
import "./styles.scss";

export default function Terms() {
  const lastUpdated = "April 2, 2026";

  const breadcrumbs = [
    { title: "Home", href: "/" },
    { title: "Legal", href: "/legal" },
    { title: "Terms of Service", href: "/terms" },
  ].map((item, index) => (
    <Anchor href={item.href} key={index} className="breadcrumb-link">
      {item.title}
    </Anchor>
  ));

  return (
    <div className="terms-page">
      <div className="terms-hero">
        <Container size="md">
          <Breadcrumbs separator={<IconChevronRight size={14} />} mb="xl" className="terms-breadcrumbs">
            {breadcrumbs}
          </Breadcrumbs>
          
          <div className="terms-title-area">
            <div className="terms-badge">
              <IconGavel size={16} />
              <span>Compliance</span>
            </div>
            <Title className="terms-main-title">Terms of Service</Title>
            <Text className="terms-last-updated">Last Updated: {lastUpdated}</Text>
          </div>
        </Container>
      </div>

      <Container size="md" className="terms-container">
        <Stack gap={60}>
          <section className="terms-section">
            <Text className="terms-intro">
              These Terms of Service govern the use of the VIREL platform operated by NAKUZISA GROUP LTD ("Company", "we", "our", or "us"). By accessing or using the VIREL mobile application, website, or related services ("Platform"), you agree to comply with these Terms.
            </Text>
          </section>

          <section className="terms-section">
            <Title order={2} className="section-heading">Eligibility</Title>
            <Text className="section-body">
              Users must be at least 18 years old and capable of entering legally binding agreements under applicable law.
            </Text>
          </section>

          <section className="terms-section">
            <Title order={2} className="section-heading">Platform Description</Title>
            <Text className="section-body">
              VIREL is a digital participation platform connecting users with promotional campaigns created by brands, organizations, or other campaign providers. Users may voluntarily participate in engagement activities such as interacting with promotional materials, providing feedback, testing services, or other forms of digital participation.
            </Text>
            <Text className="section-body mt-4">
              The Platform does not host or distribute copyrighted streaming content. Tasks involving third-party platforms are subject to those platforms’ terms and policies.
            </Text>
          </section>

          <section className="terms-section">
            <Title order={2} className="section-heading">User Accounts</Title>
            <Text className="section-body">
              Users must provide accurate information when creating accounts. Users are responsible for maintaining the security of their credentials. Accounts may not be transferred or sold.
            </Text>
          </section>

          <section className="terms-section">
            <Title order={2} className="section-heading">Task Participation</Title>
            <Text className="section-body">
              Users may participate in tasks offered through the Platform. Tasks must be completed according to provided instructions. Rewards are granted only after successful verification by platform systems.
            </Text>
          </section>

          <section className="terms-section">
            <Title order={2} className="section-heading">No Guaranteed Income</Title>
            <Text className="section-body">
              Participation on the Platform does not constitute employment, contract work, or guaranteed earnings. Rewards depend on campaign availability and successful task verification. The Company makes no guarantees regarding income, earnings potential, or reward frequency.
            </Text>
          </section>

          <section className="terms-section">
            <Title order={2} className="section-heading">Rewards</Title>
            <Text className="section-body">
              Users may earn coins, credits, or other reward values for verified participation. Reward amounts may vary by campaign and may change at any time.
            </Text>
          </section>

          <section className="terms-section">
            <Title order={2} className="section-heading">Withdrawals</Title>
            <Text className="section-body">
              Withdrawals are subject to minimum thresholds, verification checks, and fraud review. The Company reserves the right to delay or reject withdrawals where suspicious activity is detected.
            </Text>
          </section>

          <section className="terms-section">
            <Title order={2} className="section-heading">Anti-Bot and Automation Clause</Title>
            <div className="important-clause">
              <Text className="section-body">
                Users must not use bots, automation software, scripts, click farms, device emulators, or other artificial methods to simulate engagement. Accounts detected engaging in automated activity may be permanently suspended and associated rewards revoked.
              </Text>
            </div>
          </section>

          <section className="terms-section">
            <Title order={2} className="section-heading">Third-Party Platform Neutrality</Title>
            <Text className="section-body">
              VIREL provides a participation platform only. The Platform does not control or guarantee results on external digital services. Users and campaign providers must comply with the terms of any third-party services they interact with. The Company is not responsible for any violations of third-party policies committed by users or campaign providers.
            </Text>
          </section>

          <section className="terms-section">
            <Title order={2} className="section-heading">Campaign Providers</Title>
            <Text className="section-body">
              Organizations using the Platform to distribute campaigns are responsible for ensuring that their campaigns comply with applicable laws, intellectual property rights, and advertising standards.
            </Text>
          </section>

          <section className="terms-section">
            <Title order={2} className="section-heading">Political and Regulated Campaigns</Title>
            <Text className="section-body">
              Campaigns involving civic, political, or regulatory messaging must comply with applicable election and advertising laws. The Company reserves the right to impose additional review or decline such campaigns.
            </Text>
          </section>

          <section className="terms-section">
            <Title order={2} className="section-heading">Platform Discretion</Title>
            <Text className="section-body">
              The Company reserves the right to modify, suspend, or terminate any account, campaign, reward, or platform feature at its sole discretion where necessary to maintain platform integrity or comply with legal obligations.
            </Text>
          </section>

          <section className="terms-section">
            <Title order={2} className="section-heading">Intellectual Property</Title>
            <Text className="section-body">
              All platform technology, trademarks, branding, and design elements are owned by NAKUZISA GROUP LTD or its licensors.
            </Text>
          </section>

          <section className="terms-section">
            <Title order={2} className="section-heading">Limitation of Liability</Title>
            <Text className="section-body">
              To the fullest extent permitted by law, the Company shall not be liable for indirect damages, loss of profits, loss of data, or campaign performance outcomes resulting from use of the Platform.
            </Text>
          </section>

          <section className="terms-section">
            <Title order={2} className="section-heading">Governing Law</Title>
            <Text className="section-body">
              These Terms shall be governed by the laws of the Federal Republic of Nigeria.
            </Text>
          </section>

          <section className="terms-footer">
            <Title order={3} className="contact-heading">Contact Information</Title>
            <Text className="contact-body">
              NAKUZISA GROUP LTD<br />
              <Anchor href={`mailto:${AppEmail}`}>{AppEmail}</Anchor><br />
              <Anchor href={AppWebsite} target="_blank">{AppWebsite}</Anchor>
            </Text>
          </section>
        </Stack>
      </Container>

      <Footer />
    </div>
  );
}
